<template>
  <div class="step-order">
    <template v-if="!orderInfo">
      <div class="ios-group-header">步骤 3 · 确认提交</div>

      <div class="ios-card ios-list">
        <div class="ios-row">
          <span class="ios-row-label">卡密编号</span>
          <span class="ios-row-value">{{ cardCode }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">商品名称</span>
          <span class="ios-row-value">{{ cardInfo?.productName }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">目标邮箱</span>
          <span class="ios-row-value">{{ tokenInfo?.email }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">账号昵称</span>
          <span class="ios-row-value">{{ tokenInfo?.name }}</span>
        </div>
      </div>

      <div class="section-gap">
        <div v-if="error" class="ios-callout is-danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ error }}</span>
        </div>

        <div class="ios-callout is-danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>订单完成之前请不要退出 ChatGPT 登录状态，否则无法充值，造成损失自行承担！</span>
        </div>

        <div class="button-group">
          <button
            v-if="!confirming"
            @click="confirming = true"
            class="btn-filled"
            :disabled="!cardCode || !token || loading"
          >
            <span v-if="loading" class="ios-spinner"></span>
            {{ loading ? '提交中…' : '确认提交订单' }}
          </button>
          <template v-else>
            <button @click="confirming = false" class="btn-gray">
              取消
            </button>
            <button
              @click="submitOrder"
              class="btn-filled btn-destructive"
              :disabled="loading"
            >
              <span v-if="loading" class="ios-spinner"></span>
              {{ loading ? '提交中…' : '确定提交' }}
            </button>
          </template>
        </div>
      </div>
    </template>

    <template v-if="orderInfo">
      <div class="order-success ios-card" :class="{ 'is-returned': isCardReturned }">
        <div class="success-icon">
          <svg v-if="isCardReturned" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <div class="success-title">{{ orderResultTitle }}</div>
        <div class="success-subtitle">{{ orderResultSubtitle }}</div>
      </div>

      <div class="ios-group-header">订单详情</div>
      <div class="ios-card ios-list">
        <div class="ios-row">
          <span class="ios-row-label">订单编号</span>
          <span class="ios-row-value">{{ orderInfo.orderId }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">商品名称</span>
          <span class="ios-row-value">{{ orderInfo.productName }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">目标邮箱</span>
          <span class="ios-row-value">{{ orderInfo.email }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">订单状态</span>
          <span class="ios-row-value" :class="getStatusClass(orderInfo.status)">{{ getStatusText(orderInfo.status) }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">创建时间</span>
          <span class="ios-row-value">{{ formatTime(orderInfo.createTime) }}</span>
        </div>
      </div>
      <div class="ios-group-footer">
        温馨提示：到账时间一般为 30秒 到 2 分钟，高峰期延迟到 3 分钟，如充值没到账请到卡密购买处联系客服处理。
      </div>

      <div class="section-gap">
        <button @click="reset" class="btn-gray">返回首页</button>
      </div>
    </template>

    <transition name="alert-fade">
      <div v-if="progressDialogVisible" class="alert-overlay">
        <div class="ios-alert">
          <div class="ios-alert-body">
            <div class="ios-alert-icon" :class="progressDialogIconClass">
              <svg v-if="isFinalCompleted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <svg v-else-if="isFinalException || isCardReturned" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span v-else class="ios-spinner alert-spinner"></span>
            </div>
            <div class="ios-alert-title">{{ progressDialogTitle }}</div>
            <div class="ios-alert-message">{{ progressDialogSubtitle }}</div>

            <div class="alert-progress">
              <div class="ios-progress-track">
                <div
                  class="ios-progress-fill"
                  :class="{ 'is-completed': isFinalCompleted, 'is-error': isFinalException, 'is-returned': isCardReturned }"
                  :style="{ width: displayProgress + '%' }"
                ></div>
              </div>
              <div class="alert-progress-meta">
                <span>{{ displayProgress }}%</span>
                <span :class="getStatusClass(currentOrderStatus)">{{ getStatusText(currentOrderStatus) }}</span>
              </div>
            </div>

            <div class="alert-details">
              <div class="alert-detail-item">
                <span>订单编号</span>
                <strong>{{ orderInfo && orderInfo.orderId }}</strong>
              </div>
              <div class="alert-detail-item">
                <span>目标邮箱</span>
                <strong>{{ orderInfo && orderInfo.email }}</strong>
              </div>
              <div class="alert-detail-item">
                <span>当前状态</span>
                <strong :class="getStatusClass(currentOrderStatus)">{{ getStatusText(currentOrderStatus) }}</strong>
              </div>
            </div>

            <div
              v-if="pollError"
              class="alert-poll-error"
              :class="{ 'is-returned': isCardReturned }"
            >
              {{ pollError }}
            </div>
          </div>

          <button
            v-if="isFinalStatus"
            @click="closeProgressDialog"
            class="ios-alert-action"
          >
            我知道了
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { createOrder, queryOrderByCard } from '../services/api'
import {
  ORDER_STATUS,
  getOrderStatusClass,
  getOrderStatusText,
  normalizeOrderStatus
} from '../constants/orderStatus'

const PROGRESS_DURATION = 30000
const PROGRESS_INTERVAL = 200
const POLL_INTERVAL = 3000
const RECHARGE_PROGRESS_TIPS = [
  '正在验证是否已有订阅',
  '正在获取支付信息',
  '正在提交支付',
  '正在充值中',
  '正在等待支付结果'
]

export default {
  name: 'StepOrder',
  props: {
    cardCode: {
      type: String,
      default: ''
    },
    token: {
      type: String,
      default: ''
    },
    cardInfo: {
      type: Object,
      default: null
    },
    tokenInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      orderInfo: null,
      loading: false,
      error: '',
      confirming: false,
      progressDialogVisible: false,
      progressValue: 0,
      pollError: '',
      progressTimer: null,
      pollTimer: null,
      progressStartedAt: 0
    }
  },
  computed: {
    currentOrderStatus() {
      return this.orderInfo ? normalizeOrderStatus(this.orderInfo.status) : ORDER_STATUS.PENDING
    },
    isFinalCompleted() {
      return this.currentOrderStatus === ORDER_STATUS.COMPLETED
    },
    isFinalException() {
      return this.currentOrderStatus === ORDER_STATUS.EXCEPTION
    },
    isCardReturned() {
      return this.currentOrderStatus === ORDER_STATUS.CARD_RETURNED
    },
    isFinalStatus() {
      return this.isFinalCompleted || this.isFinalException || this.isCardReturned
    },
    displayProgress() {
      return Math.round(this.progressValue)
    },
    progressDialogTitle() {
      if (this.isFinalCompleted) return '充值完成'
      if (this.isCardReturned) return '充值失败，卡密已退还'
      if (this.isFinalException) return '充值异常'
      return '正在充值'
    },
    currentProgressTip() {
      const progressStep = 99 / RECHARGE_PROGRESS_TIPS.length
      const tipIndex = Math.min(
        RECHARGE_PROGRESS_TIPS.length - 1,
        Math.floor(this.progressValue / progressStep)
      )

      return RECHARGE_PROGRESS_TIPS[tipIndex]
    },
    progressDialogSubtitle() {
      if (this.isFinalCompleted) return '后台已确认订单完成'
      if (this.isCardReturned) return '订单未完成，原卡密已退回未兑换状态'
      if (this.isFinalException) return '后台返回异常结果，请联系购卡处处理'
      return this.currentProgressTip
    },
    progressDialogIconClass() {
      if (this.isFinalCompleted) return 'is-completed'
      if (this.isCardReturned) return 'is-returned'
      if (this.isFinalException) return 'is-error'
      return 'is-running'
    },
    orderResultTitle() {
      if (this.isCardReturned) return '充值失败，卡密已退还'
      return '订单提交成功'
    },
    orderResultSubtitle() {
      if (this.isCardReturned) return '原卡密已恢复为未兑换状态，可重新提交'
      return '充值任务已进入处理队列'
    }
  },
  beforeDestroy() {
    this.stopProgressTracking()
  },
  methods: {
    async submitOrder() {
      this.loading = true
      this.orderInfo = null
      this.error = ''
      this.pollError = ''

      const response = await createOrder(this.cardCode, this.token)

      if (response.code === 200) {
        this.orderInfo = response.data
        this.$emit('completed', response.data)
        this.openProgressDialog()
      } else {
        this.error = response.message || '订单提交失败'
      }

      this.loading = false
    },
    getStatusClass(status) {
      return getOrderStatusClass(status)
    },
    getStatusText(status) {
      return getOrderStatusText(status)
    },
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    openProgressDialog() {
      this.progressDialogVisible = true
      this.startProgressTracking()
    },
    closeProgressDialog() {
      this.progressDialogVisible = false
    },
    startProgressTracking() {
      this.stopProgressTracking()
      this.progressValue = this.isFinalStatus ? 100 : 0
      this.progressStartedAt = Date.now()

      if (!this.isFinalStatus) {
        this.progressTimer = setInterval(this.updateProgress, PROGRESS_INTERVAL)
        this.pollTimer = setInterval(this.pollOrderResult, POLL_INTERVAL)
        this.pollOrderResult()
      }
    },
    stopProgressTracking() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    updateProgress() {
      if (this.isFinalStatus) {
        this.progressValue = 100
        this.stopProgressTracking()
        return
      }

      const elapsed = Date.now() - this.progressStartedAt
      this.progressValue = Math.min(99, (elapsed / PROGRESS_DURATION) * 99)
    },
    async pollOrderResult() {
      if (this.isFinalStatus) return

      const response = await queryOrderByCard(this.cardCode)

      if (response.code === 200 && response.data) {
        this.pollError = ''
        this.orderInfo = {
          ...this.orderInfo,
          ...response.data
        }

        if (this.isFinalStatus) {
          this.progressValue = 100
          this.stopProgressTracking()
        }
      } else if (response.data && response.data.found === false) {
        this.markCardReturned(response.data.message || response.message)
      } else {
        this.pollError = response.message || '订单结果查询失败，系统将继续重试'
      }
    },
    markCardReturned(message) {
      this.orderInfo = {
        ...this.orderInfo,
        status: ORDER_STATUS.CARD_RETURNED,
        returnedMessage: message
      }
      this.progressValue = 100
      this.pollError = '充值失败，卡密已自动退回，可重新使用原卡密提交订单或联系购卡处。'
      this.stopProgressTracking()
    },
    reset() {
      this.stopProgressTracking()
      this.$emit('reset')
    }
  }
}
</script>

<style scoped>
.section-gap {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 18px;
}

.button-group {
  display: flex;
  gap: 12px;
}

.button-group .btn-gray {
  flex: 1;
}

.button-group .btn-filled {
  flex: 2;
}

.button-group .btn-filled:only-child {
  flex: 1;
}

/* ===== success view ===== */
.order-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px 20px;
  margin-top: 18px;
  animation: fadeIn 0.35s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.success-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(52, 199, 89, 0.12);
  color: var(--ios-green);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  animation: popIn 0.4s ease;
}

.order-success.is-returned .success-icon {
  background: rgba(255, 149, 0, 0.12);
  color: var(--ios-orange);
}

@keyframes popIn {
  0% {
    transform: scale(0);
  }
  60% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}

.success-icon svg {
  width: 30px;
  height: 30px;
}

.success-title {
  font-size: 20px;
  font-weight: 700;
}

.success-subtitle {
  font-size: 14px;
  color: var(--ios-label-secondary);
}

/* ===== iOS alert-style progress dialog ===== */
.alert-overlay {
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.35);
}

.alert-fade-enter-active,
.alert-fade-leave-active {
  transition: opacity 0.25s ease;
}

.alert-fade-enter-active .ios-alert,
.alert-fade-leave-active .ios-alert {
  transition: transform 0.25s ease;
}

.alert-fade-enter,
.alert-fade-leave-to {
  opacity: 0;
}

.alert-fade-enter .ios-alert {
  transform: scale(1.08);
}

.ios-alert {
  width: min(100%, 320px);
  border-radius: 18px;
  background: rgba(250, 250, 250, 0.95);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.ios-alert-body {
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.ios-alert-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.ios-alert-icon svg {
  width: 28px;
  height: 28px;
}

.ios-alert-icon.is-running {
  background: rgba(0, 122, 255, 0.1);
  color: var(--ios-blue);
}

.ios-alert-icon.is-completed {
  background: rgba(52, 199, 89, 0.12);
  color: var(--ios-green);
}

.ios-alert-icon.is-error {
  background: rgba(255, 59, 48, 0.1);
  color: var(--ios-red);
}

.ios-alert-icon.is-returned {
  background: rgba(255, 149, 0, 0.12);
  color: var(--ios-orange);
}

.alert-spinner {
  width: 26px;
  height: 26px;
}

.ios-alert-title {
  font-size: 17px;
  font-weight: 600;
}

.ios-alert-message {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--ios-label-secondary);
  min-height: 20px;
}

.alert-progress {
  width: 100%;
  margin-top: 18px;
}

.ios-progress-fill.is-completed {
  background: var(--ios-green);
}

.ios-progress-fill.is-error {
  background: var(--ios-red);
}

.ios-progress-fill.is-returned {
  background: var(--ios-orange);
}

.alert-progress-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ios-label-secondary);
}

.alert-details {
  width: 100%;
  margin-top: 16px;
  border-radius: 12px;
  background: rgba(118, 118, 128, 0.08);
  overflow: hidden;
}

.alert-detail-item {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 12px;
  font-size: 13px;
  position: relative;
}

.alert-detail-item + .alert-detail-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 12px;
  right: 0;
  height: 1px;
  background: var(--ios-separator);
  transform: scaleY(0.5);
}

.alert-detail-item span {
  color: var(--ios-label-secondary);
  flex-shrink: 0;
}

.alert-detail-item strong {
  font-weight: 600;
  text-align: right;
  word-break: break-all;
}

.alert-poll-error {
  width: 100%;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 149, 0, 0.12);
  color: #8a5300;
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
}

.alert-poll-error.is-returned {
  background: rgba(255, 149, 0, 0.12);
  color: #8a5300;
  text-align: center;
}

.ios-alert-action {
  width: 100%;
  min-height: 46px;
  border: none;
  border-top: 1px solid var(--ios-separator);
  background: transparent;
  color: var(--ios-blue);
  font-size: 17px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.ios-alert-action:active {
  background: rgba(118, 118, 128, 0.12);
}

@media (max-width: 600px) {
  .button-group {
    flex-direction: column;
  }
}
</style>
